/**
 * Metadatový katalog SOAP Tools operací (eCobra SOAP konektor). 9 operací ve
 * 4 skupinách (OTP / Device / SMS / Authorization). Runner (SoapToolsPage)
 * renderuje pole genericky, `buildSoapBody` sesbírá hodnoty a POSTne na
 * `/admin/api/v1/tools/soap/execute/{operation}`. Mirror {@link ../apiTools/catalog}.
 */

export type SoapFieldKind = "text" | "textarea" | "number" | "select" | "csv";

export interface SoapFieldSpec {
  name: string;
  /** i18n klíč labelu. */
  label: string;
  kind: SoapFieldKind;
  required?: boolean;
  default?: string;
  placeholder?: string;
  /** select: pevné volby (value + i18n label klíč nebo přímý text). */
  options?: { value: string; label: string }[];
  /** select: hodnota `"true"`/`"false"` → boolean místo string. */
  boolean?: boolean;
  /** number: prázdno → `null` (jinak `fallback` nebo `null`). */
  nullable?: boolean;
  /** number: prázdno → tato hodnota. */
  fallback?: number;
}

export type SoapGroup = "otp" | "device" | "sms" | "authorization";

export interface SoapOperationSpec {
  id: string;
  group: SoapGroup;
  /** i18n klíč. */
  label: string;
  fields: SoapFieldSpec[];
}

export interface SoapGroupSpec {
  group: SoapGroup;
  /** i18n klíč názvu skupiny. */
  label: string;
  operations: SoapOperationSpec[];
}

const OS_OPTIONS = [
  { value: "Android", label: "Android" },
  { value: "iOS", label: "iOS" },
  { value: "Huawei", label: "Huawei" },
];

const identityField: SoapFieldSpec = {
  name: "identity",
  label: "soaptools.f.identity",
  kind: "text",
  required: true,
  default: "50000002",
};

const OTP_OPERATIONS: SoapOperationSpec[] = [
  {
    id: "generateOtp",
    group: "otp",
    label: "soaptools.op.generateOtp",
    fields: [
      identityField,
      { name: "deviceOSType", label: "soaptools.f.deviceOSType", kind: "select", required: true, default: "Android", options: OS_OPTIONS },
      { name: "reason", label: "soaptools.f.reason", kind: "text", default: "mobileTokenActivation" },
      {
        name: "sendSms",
        label: "soaptools.f.sendSms",
        kind: "select",
        boolean: true,
        default: "true",
        options: [
          { value: "true", label: "soaptools.opt.yes" },
          { value: "false", label: "soaptools.opt.no" },
        ],
      },
    ],
  },
  {
    id: "getUserOtp",
    group: "otp",
    label: "soaptools.op.getUserOtp",
    fields: [identityField],
  },
];

const DEVICE_OPERATIONS: SoapOperationSpec[] = [
  {
    id: "getInstanceInfo",
    group: "device",
    label: "soaptools.op.getInstanceInfo",
    fields: [
      identityField,
      { name: "device", label: "soaptools.f.device", kind: "text", required: true, placeholder: "device-uuid" },
      { name: "deviceOSType", label: "soaptools.f.deviceOSType", kind: "select", default: "Android", options: OS_OPTIONS },
      { name: "deviceType", label: "soaptools.f.deviceType", kind: "number", default: "1", fallback: 1 },
      { name: "appVersionCode", label: "soaptools.f.appVersionCode", kind: "text", default: "2.0.0" },
    ],
  },
  {
    id: "getUserDeviceList",
    group: "device",
    label: "soaptools.op.getUserDeviceList",
    fields: [
      { name: "identities", label: "soaptools.f.identities", kind: "csv", required: true, default: "50000002" },
      { name: "filterStatus", label: "soaptools.f.filterStatus", kind: "number", nullable: true, placeholder: "1 = Active only" },
    ],
  },
];

const SMS_OPERATIONS: SoapOperationSpec[] = [
  {
    id: "getUserSmsPhoneNumber",
    group: "sms",
    label: "soaptools.op.getUserSmsPhoneNumber",
    fields: [identityField],
  },
  {
    id: "setUserSmsPhoneNumber",
    group: "sms",
    label: "soaptools.op.setUserSmsPhoneNumber",
    fields: [
      identityField,
      { name: "smsPhoneNumber", label: "soaptools.f.smsPhoneNumber", kind: "text", required: true, placeholder: "+420777123456" },
    ],
  },
];

const AUTH_OPERATIONS: SoapOperationSpec[] = [
  {
    id: "decryptSecureMessages",
    group: "authorization",
    label: "soaptools.op.decryptSecureMessages",
    fields: [
      identityField,
      { name: "device", label: "soaptools.f.device", kind: "text", required: true },
      { name: "deviceType", label: "soaptools.f.deviceType", kind: "number", default: "1", fallback: 1 },
      { name: "secureChannelMessage", label: "soaptools.f.secureChannelMessage", kind: "textarea", required: true },
    ],
  },
  {
    id: "authorizationChallenge",
    group: "authorization",
    label: "soaptools.op.authorizationChallenge",
    fields: [
      identityField,
      { name: "device", label: "soaptools.f.device", kind: "text", required: true },
      { name: "deviceType", label: "soaptools.f.deviceType", kind: "number", default: "1", fallback: 1 },
      { name: "operationType", label: "soaptools.f.operationType", kind: "text", default: "payment" },
      { name: "hash", label: "soaptools.f.hash", kind: "text", required: true },
      { name: "title", label: "soaptools.f.title", kind: "text", default: "Payment Authorization" },
      { name: "signedDataFields", label: "soaptools.f.signedDataFields", kind: "csv", default: "amount,account" },
      { name: "signedDataLabels", label: "soaptools.f.signedDataLabels", kind: "csv", default: "Amount,Account" },
    ],
  },
  {
    id: "authorizationProcess",
    group: "authorization",
    label: "soaptools.op.authorizationProcess",
    fields: [
      identityField,
      { name: "device", label: "soaptools.f.device", kind: "text", required: true },
      { name: "deviceType", label: "soaptools.f.deviceType", kind: "number", default: "1", fallback: 1 },
      { name: "signId", label: "soaptools.f.signId", kind: "number", required: true },
      { name: "hash", label: "soaptools.f.hash", kind: "text", required: true },
      { name: "signature", label: "soaptools.f.signature", kind: "text", required: true },
      {
        name: "signatureType",
        label: "soaptools.f.signatureType",
        kind: "select",
        default: "MacCode",
        options: [
          { value: "MacCode", label: "MacCode" },
          { value: "SecureMessagePayload", label: "SecureMessagePayload" },
        ],
      },
      { name: "title", label: "soaptools.f.title", kind: "text", default: "Payment Authorization" },
      { name: "signedDataFields", label: "soaptools.f.signedDataFields", kind: "csv", default: "amount,account" },
      { name: "signedDataLabels", label: "soaptools.f.signedDataLabels", kind: "csv", default: "Amount,Account" },
    ],
  },
];

export const SOAP_GROUPS: SoapGroupSpec[] = [
  { group: "otp", label: "soaptools.group.otp", operations: OTP_OPERATIONS },
  { group: "device", label: "soaptools.group.device", operations: DEVICE_OPERATIONS },
  { group: "sms", label: "soaptools.group.sms", operations: SMS_OPERATIONS },
  { group: "authorization", label: "soaptools.group.authorization", operations: AUTH_OPERATIONS },
];

/**
 * Sestaví request body z hodnot polí (1:1 s legacy `collectFormData`): csv →
 * pole, number → int/null s fallbackem, boolean select → boolean, jinak string.
 */
export function buildSoapBody(op: SoapOperationSpec, values: Record<string, unknown>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const f of op.fields) {
    const raw = values[f.name];
    switch (f.kind) {
      case "csv":
        body[f.name] = String(raw ?? "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        break;
      case "number": {
        const s = String(raw ?? "").trim();
        if (s === "") {
          body[f.name] = f.nullable ? null : f.fallback ?? null;
        } else {
          const n = parseInt(s, 10);
          body[f.name] = Number.isNaN(n) ? (f.nullable ? null : f.fallback ?? null) : n;
        }
        break;
      }
      case "select":
        body[f.name] = f.boolean ? String(raw ?? "") === "true" : String(raw ?? "");
        break;
      default:
        body[f.name] = String(raw ?? "");
    }
  }
  return body;
}
